// src/layouts/settings/index.js
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { notifyCenter } from "utils/toast";
import { setAppKeys } from "utils/appKeys";
 
import {
  getBmaxSettings,
  saveSlider1Settings,
  saveSlider2Settings,
  getCustomConcrete,
  saveCustomConcrete,
  saveExtraSettings,
  getSmtpSettings,
  saveSmtpSettings,
  getOtherSettings,
  saveOtherSettings,
} from "api/apiService";

const TABS = [
  { key: "slider1", label: "Slider 1" },
  { key: "slider2", label: "Slider 2" },
  { key: "customConcrete", label: "Spesialtilpasset betong" },
  { key: "extra", label: "Ekstra" },
  { key: "smtp", label: "SMTP-innstillinger" },
  { key: "other", label: "Andre innstillinger" },
];

// --- sizes: smaller ID, bigger Name, smaller selects ---
const COL_TPL = {
  eq: "80px 1.7fr repeat(4, 136px) 44px",
  car: "80px 1.7fr repeat(4, 136px) 44px",
  preset: "70px 2.1fr 136px 136px 136px 136px 136px 136px 136px 44px",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "10px", height: 44 },
  "& .MuiInputLabel-outlined": { fontWeight: 500 },
};
const compactFieldSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": { borderRadius: "10px", height: 40 },
  "& .MuiInputBase-input": { py: 0.75 },
};
const numberInputSx = {
  ...compactFieldSx,
  "& .MuiOutlinedInput-root": { position: "relative" },
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& .MuiOutlinedInput-input": { paddingRight: "36px" },
};

const RowShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string.isRequired,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  active: PropTypes.bool,
  _delete: PropTypes.bool,
});

// ---------- Reusable UI bits ----------
const ScrollX = ({ minWidth = 1200, children }) => (
  <SoftBox sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch", pb: 1 }}>
    <Box sx={{ minWidth }}>{children}</Box>
  </SoftBox>
);
ScrollX.propTypes = { minWidth: PropTypes.number, children: PropTypes.node };

function GridHeader({ columns, template }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: template, gap: 1, px: 0.5, pb: 0.75 }}>
      {columns.map((c, idx) => (
        <SoftTypography key={idx} variant="caption" sx={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.4 }}>
          {c}
        </SoftTypography>
      ))}
    </Box>
  );
}
GridHeader.propTypes = { columns: PropTypes.array.isRequired, template: PropTypes.string.isRequired };

function NumberWithStepper({ value, onChange, placeholder }) {
  const step = (dir) => {
    const curr = Number(value || 0);
    const next = (isNaN(curr) ? 0 : curr) + (dir === "up" ? 1 : -1);
    onChange(String(next));
  };

  return (
    <TextField
      placeholder={placeholder}
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      sx={numberInputSx}
      InputProps={{
        endAdornment: (
          <Box
            sx={{
              position: "absolute",
              right: 6,
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <IconButton size="small" onClick={() => step("up")} sx={{ p: 0, width: 20, height: 16 }}>
              <ArrowDropUpIcon fontSize="inherit" />
            </IconButton>
            <IconButton size="small" onClick={() => step("down")} sx={{ p: 0, width: 20, height: 16, mt: "-2px" }}>
              <ArrowDropDownIcon fontSize="inherit" />
            </IconButton>
          </Box>
        ),
      }}
    />
  );
}
NumberWithStepper.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};
NumberWithStepper.defaultProps = { value: "", placeholder: "" };

function SelectId({ value, onChange, options, placeholder = "Velg …" }) {
  return (
    <TextField
      select
      fullWidth
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      sx={compactFieldSx}
      SelectProps={{
        displayEmpty: true,
        renderValue: (selected) => {
          if (selected === "" || selected === undefined || selected === null) {
            return <span style={{ color: "#9ca3af" }}>{placeholder}</span>;
          }
          const item = options.find((o) => String(o.id) === String(selected));
          return item?.name ?? String(selected);
        },
      }}
    >
      <MenuItem value="" disabled>
        {placeholder}
      </MenuItem>
      {options.map((opt) => (
        <MenuItem key={opt.id} value={String(opt.id)}>
          {opt.name}
        </MenuItem>
      ))}
    </TextField>
  );
}
SelectId.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
};

const Label = ({ children }) => (
  <SoftTypography variant="caption" sx={{ fontWeight: 700, color: "#111", mb: 0.5, display: "block" }}>
    {children}
  </SoftTypography>
);
Label.propTypes = { children: PropTypes.node };

// ---------- Section (Custom Concrete) ----------
function Section({ title, rows, setRows }) {
  // equal widths for “Navn” and “Pris”, compact ID
  const TEMPLATE = "120px 1fr 1fr 44px";

  const updRow = (i, k, v) =>
    setRows((p) => {
      const n = [...p];
      n[i] = { ...n[i], [k]: v };
      return n;
    });

  const canRemoveAt = (all, i) => all.filter((r) => !r._delete).length > 1 && !all[i]?._delete;

  const markDelAt = (all, i) => {
    const n = [...all];
    if (n[i]?.id) n[i] = { ...n[i], _delete: true };
    else n.splice(i, 1);
    return n;
  };

  return (
    <SoftBox
      p={2.25}
      sx={{
        borderRadius: 2,
        background: "#fff",
        boxShadow: "0 8px 22px rgba(15,23,42,.06)",
        border: "1px solid rgba(2,6,23,.06)",
        mb: 3,
      }}
    >
      <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <SoftTypography variant="h6" fontWeight="bold">
          {title}
        </SoftTypography>
        <Tooltip title="Legg til rad">
          <SoftButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => setRows((p) => [...p, { id: undefined, name: "", price: "" }])}
            startIcon={<AddIcon />}
          >
            Legg til
          </SoftButton>
        </Tooltip>
      </SoftBox>

      <ScrollX minWidth={720}>
        <GridHeader columns={["ID", "Navn", "Pris", ""]} template={TEMPLATE} />
        <Box sx={{ display: "grid", gap: 1.25 }}>
          {rows.map((r, i) => {
            const canRemove = canRemoveAt(rows, i);
            return (
              <Box
                key={r.id ?? `row-${i}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: TEMPLATE,
                  gap: 1,
                  opacity: r._delete ? 0.5 : 1,
                }}
              >
                <TextField value={r.id ? String(r.id) : "Ny"} InputProps={{ readOnly: true }} fullWidth sx={compactFieldSx} />
                <TextField
                  placeholder="Navn"
                  value={r.name}
                  onChange={(e) => updRow(i, "name", e.target.value)}
                  fullWidth
                  sx={compactFieldSx}
                />
                <NumberWithStepper value={r.price} onChange={(v) => updRow(i, "price", v)} placeholder="0" />
                <Tooltip title={canRemove ? "Fjern" : ""}>
                  <span>
                    <IconButton
                      color="error"
                      onClick={() => setRows((p) => markDelAt(p, i))}
                      size="small"
                      disabled={!canRemove}
                      sx={{ visibility: canRemove ? "visible" : "hidden", mt: "2px" }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      </ScrollX>
    </SoftBox>
  );
}
Section.propTypes = {
  title: PropTypes.node.isRequired,
  rows: PropTypes.arrayOf(RowShape).isRequired,
  setRows: PropTypes.func.isRequired,
};
Section.defaultProps = { rows: [] };

// ---------- Main component ----------
function Settings() {
  const [activeTab, setActiveTab] = useState("slider1");
  const [prevTab, setPrevTab] = useState("slider1");
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [qualityRows, setQualityRows] = useState([]);
  const [reductionRows, setReductionRows] = useState([]);
  const [stoneRows, setStoneRows] = useState([]);
  const [consistencyRows, setConsistencyRows] = useState([]);
  // Extra
  const [extraAddOpts, setExtraAddOpts] = useState([]);        // additionalOptions
  // Ventetidsmeldingen kunden ser over datovalget i bestillingen. Ligger her og
  // ikke i koden fordi tallet endrer seg med sesongen.
  const [leadTimeNote, setLeadTimeNote] = useState("");
  const [extraConcAdds, setExtraConcAdds] = useState([]);      // concreteAdditions

  const [tabLoading, setTabLoading] = useState(false);

  const [smtp, setSmtp] = useState({
    host: "",
    port: "",
    user: "",
    password: "",
    encryption: "SSL",
    fromName: "",
    fromEmail: "",
  });
  const [encryptionOpen, setEncryptionOpen] = useState(false);

  const [other, setOther] = useState({
    shareApiKey: "",
    googleMapsApiKey: "",
    weatherApiKey: "",
  });

  const toNum = (v) => {
    const n = Number(String(v ?? "").trim());
    return Number.isFinite(n) ? String(n) : "0";
  };
  const hasAny = (obj, keys) => keys.some((k) => String(obj[k] ?? "").trim() !== "");

  // Slider 1
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [distanceRate, setDistanceRate] = useState({ id: undefined, basePrice: "", pricePerKm: "" });
  const [timeRates, setTimeRates] = useState([]);

  // Slider 2
  const [equipRows, setEquipRows] = useState([]);
  const [carRows, setCarRows] = useState([]);
  const [presetRows, setPresetRows] = useState([]);

  // dropdown options
  const [concreteOpts, setConcreteOpts] = useState({ quality: [], stone: [], reduction: [], consistency: [] });

  const pickTimeRates = (resObj) => {
    const s = resObj?.data?.slider1 || {};
    return s.timeRates ?? s.timerates ?? s.timeRate ?? s.timerate ?? [];
  };

  // fetch all once
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await getBmaxSettings();

        const dr = res?.data?.slider1?.distanceRate || {};
        const trArr = pickTimeRates(res);

        setDistanceRate({
          id: dr.id,
          basePrice: String(dr.basePrice ?? ""),
          pricePerKm: String(dr.pricePerKm ?? ""),
        });

        const normalized = (Array.isArray(trArr) ? trArr : []).map((r) => ({
          id: r.id,
          delivery: r.delivery || "",
          price: String(r.price ?? ""),
        }));
        setTimeRates(normalized.length ? normalized : [{ id: undefined, delivery: "", price: "" }]);

        const s2 = res?.data?.slider2 || {};
        const eq = Array.isArray(s2.equipment) ? s2.equipment : [];
        const ct = Array.isArray(s2.carTypes) ? s2.carTypes : [];
        const cp = Array.isArray(s2.concretePresets) ? s2.concretePresets : [];

        setEquipRows(
          eq.length
            ? eq.map((r) => ({
                id: r.id,
                name: r.name || "",
                equipmentPrice: String(r.equipmentPrice ?? ""),
                equipmentHourlyRate: String(r.equipmentHourlyRate ?? ""),
                equipmentMinVolume: String(r.equipmentMinVolume ?? ""),
                equipmentMaxVolume: String(r.equipmentMaxVolume ?? ""),
              }))
            : [
                {
                  id: undefined,
                  name: "",
                  equipmentPrice: "",
                  equipmentHourlyRate: "",
                  equipmentMinVolume: "",
                  equipmentMaxVolume: "",
                },
              ]
        );

        setCarRows(
          ct.length
            ? ct.map((r) => ({
                id: r.id,
                name: r.name || "",
                vehicleMinCost: String(r.vehicleMinCost ?? ""),
                vehicleHourlyRate: String(r.vehicleHourlyRate ?? ""),
                vehicleIncludedHours: String(r.vehicleIncludedHours ?? ""),
                vehicleMinVolume: String(r.vehicleMinVolume ?? ""),
                vehicleMaxVolume: String(r.vehicleMaxVolume ?? ""),
              }))
            : [{ id: undefined, name: "", vehicleMinCost: "", vehicleHourlyRate: "", vehicleIncludedHours: "", vehicleMinVolume: "", vehicleMaxVolume: "" }]
        );

        setPresetRows(
          cp.length
            ? cp.map((r) => ({
                id: r.id,
                name: r.name || "",
                qualityId: String(r.qualityId ?? ""),
                stoneTypeId: String(r.stoneTypeId ?? ""),
                reductionId: String(r.reductionId ?? ""),
                consistencyId: String(r.consistencyId ?? ""),
                deliveryPer15Min: String(r.deliveryPer15Min ?? ""),
                priceBufferPercent: String(r.priceBufferPercent ?? ""),
                pumpingHourlyRate: String(r.pumpingHourlyRate ?? ""),
              }))
            : [
                {
                  id: undefined,
                  name: "",
                  qualityId: "",
                  stoneTypeId: "",
                  reductionId: "",
                  consistencyId: "",
                  deliveryPer15Min: "",
                  priceBufferPercent: "",
                  pumpingHourlyRate: "",
                },
              ]
        );

        const extra = res?.data?.extra || {};
        const asExtraRows = (arr) =>
          (Array.isArray(arr) ? arr : []).map((r) => ({
            id: r.id,
            name: r.name ?? "",
            price: String(r.price ?? ""),
            active: !!r.active,
          }));

        setExtraAddOpts(asExtraRows(extra.additionalOptions));
        setLeadTimeNote(extra.deliveryLeadTimeNote ?? "");
        setExtraConcAdds(asExtraRows(extra.concreteAdditions));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // options
  useEffect(() => {
    (async () => {
      try {
        const res = await getCustomConcrete();
        const d = res?.data ?? res;

        const asRows = (arr) =>
          (Array.isArray(arr) ? arr : []).map((r) => ({
            id: r.id,
            name: r.name ?? r.title ?? "",
            price: String(r.price ?? r.cost ?? 0),
          }));

        setConcreteOpts({
          quality: d?.concrete_quality ?? [],
          stone: d?.concrete_stone ?? [],
          reduction: d?.concrete_reduction ?? [],
          consistency: d?.concrete_consistency ?? [],
        });

        // editable copies for Tab 3
        setQualityRows(asRows(d?.concrete_quality));
        setStoneRows(asRows(d?.concrete_stone));
        setReductionRows(asRows(d?.concrete_reduction));
        setConsistencyRows(asRows(d?.concrete_consistency));
      } catch (e) {
        console.error("Error fetching custom concrete:", e);
      }
    })();
  }, []);

  // helpers S1
  const setDR = (k, v) => setDistanceRate((p) => ({ ...p, [k]: v }));
  const addTimeRow = () => setTimeRates((prev) => [...prev, { id: undefined, delivery: "", price: "" }]);
  const updateTimeRow = (idx, key, value) =>
    setTimeRates((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  const activeRowsCount = () => timeRates.filter((r) => !r._delete).length;
  const removeTimeRow = (idx) =>
    setTimeRates((prev) => {
      if (prev.filter((r) => !r._delete).length <= 1) return prev;
      const next = [...prev];
      if (next[idx]?.id) next[idx] = { ...next[idx], _delete: true };
      else next.splice(idx, 1);
      return next;
    });
  const hhmmOk = (s) => /^([01]\d|2[0-3]):[0-5]\d$/.test((s || "").trim());

  const cleanPayloadS1 = () => {
    const rows = timeRates
      .filter((r) => r._delete || r.delivery || r.price !== "")
      .map((r) => ({
        id: r.id,
        delivery: (r.delivery || "").trim(),
        price: r.price,
        ...(r._delete ? { _delete: true } : {}),
      }));

    return {
      distanceRate: {
        id: distanceRate.id,
        basePrice: distanceRate.basePrice,
        pricePerKm: distanceRate.pricePerKm,
      },
      timeRates: rows,
    };
  };

  // helpers S2
  const addEquipRow = () =>
    setEquipRows((p) => [
      ...p,
      { id: undefined, name: "", equipmentPrice: "", equipmentHourlyRate: "", equipmentMinVolume: "", equipmentMaxVolume: "" },
    ]);
  const updEquip = (i, k, v) => setEquipRows((p) => ((p = [...p]), (p[i] = { ...p[i], [k]: v }), p));
  const delEquip = (i) =>
    setEquipRows((p) => {
      if (p.filter((r) => !r._delete).length <= 1) return p;
      const n = [...p];
      if (n[i]?.id) n[i] = { ...n[i], _delete: true };
      else n.splice(i, 1);
      return n;
    });

  const addCarRow = () =>
    setCarRows((p) => [
      ...p,
      { id: undefined, name: "", vehicleMinCost: "", vehicleHourlyRate: "", vehicleIncludedHours: "", vehicleMinVolume: "", vehicleMaxVolume: "" },
    ]);
  const updCar = (i, k, v) => setCarRows((p) => ((p = [...p]), (p[i] = { ...p[i], [k]: v }), p));
  const delCar = (i) =>
    setCarRows((p) => {
      if (p.filter((r) => !r._delete).length <= 1) return p;
      const n = [...p];
      if (n[i]?.id) n[i] = { ...n[i], _delete: true };
      else n.splice(i, 1);
      return n;
    });

  const addPresetRow = () =>
    setPresetRows((p) => [
      ...p,
      {
        id: undefined,
        name: "",
        qualityId: "",
        stoneTypeId: "",
        reductionId: "",
        consistencyId: "",
        deliveryPer15Min: "",
        priceBufferPercent: "",
        pumpingHourlyRate: "",
      },
    ]);
  const updPreset = (i, k, v) => setPresetRows((p) => ((p = [...p]), (p[i] = { ...p[i], [k]: v }), p));
  const delPreset = (i) =>
    setPresetRows((p) => {
      if (p.filter((r) => !r._delete).length <= 1) return p;
      const n = [...p];
      if (n[i]?.id) n[i] = { ...n[i], _delete: true };
      else n.splice(i, 1);
      return n;
    });

  const cleanPayloadS2 = () => {
    const equipments = equipRows
      .filter((r) => r._delete || hasAny(r, ["name", "equipmentPrice", "equipmentHourlyRate", "equipmentMinVolume", "equipmentMaxVolume"]))
      .map((r) => ({
        id: r.id,
        name: (r.name || "").trim(),
        equipmentPrice: toNum(r.equipmentPrice),
        equipmentHourlyRate: toNum(r.equipmentHourlyRate),
        equipmentMinVolume: toNum(r.equipmentMinVolume),
        equipmentMaxVolume: toNum(r.equipmentMaxVolume),
        ...(r._delete ? { _delete: true } : {}),
      }));

    const carTypes = carRows
      .filter((r) => r._delete || hasAny(r, ["name", "vehicleMinCost", "vehicleHourlyRate", "vehicleIncludedHours", "vehicleMinVolume", "vehicleMaxVolume"]))
      .map((r) => ({
        id: r.id,
        name: (r.name || "").trim(),
        vehicleMinCost: toNum(r.vehicleMinCost),
        vehicleHourlyRate: toNum(r.vehicleHourlyRate),
        vehicleIncludedHours: toNum(r.vehicleIncludedHours),
        vehicleMinVolume: toNum(r.vehicleMinVolume),
        vehicleMaxVolume: toNum(r.vehicleMaxVolume),
        ...(r._delete ? { _delete: true } : {}),
      }));

    const concretePresets = presetRows
      .filter((r) => r._delete || hasAny(r, ["name", "qualityId", "stoneTypeId", "reductionId", "consistencyId", "deliveryPer15Min", "priceBufferPercent", "pumpingHourlyRate"]))
      .map((r) => ({
        id: r.id,
        name: (r.name || "").trim(),
        qualityId: Number(r.qualityId || 0) || 0,
        stoneTypeId: Number(r.stoneTypeId || 0) || 0,
        reductionId: Number(r.reductionId || 0) || 0,
        consistencyId: Number(r.consistencyId || 0) || 0,
        deliveryPer15Min: toNum(r.deliveryPer15Min),
        priceBufferPercent: toNum(r.priceBufferPercent),
        pumpingHourlyRate: toNum(r.pumpingHourlyRate),
        ...(r._delete ? { _delete: true } : {}),
      }));

    return { equipments, carTypes, concretePresets };
  };

  const onSaveS1 = async () => {
    const bad = timeRates.filter((r) => !r._delete && r.delivery && !hhmmOk(r.delivery));
    if (bad.length) {
      alert("Leveringstid må være HH:MM (24t).");
      return;
    }
    setSaving(true);
    try {
      const payload = cleanPayloadS1();
      const res = await saveSlider1Settings(payload);
      if (!res?.ok) throw new Error("Save failed");

      const refreshed = await getBmaxSettings();
      const dr = refreshed?.data?.slider1?.distanceRate || {};
      const trArray = pickTimeRates(refreshed);
      setDistanceRate({ id: dr.id, basePrice: String(dr.basePrice ?? ""), pricePerKm: String(dr.pricePerKm ?? "") });
      setTimeRates(trArray.length ? trArray.map((r) => ({ id: r.id, delivery: r.delivery || "", price: String(r.price ?? "") })) : [{ id: undefined, delivery: "", price: "" }]);
      notifyCenter("success", "Innstillingene ble lagret!", { duration: 2000 });
    } catch (e) {
      console.error(e);
      notifyCenter("error", "Kunne ikke lagre innstillingene.", { duration: 2000 });
    } finally {
      setSaving(false);
    }
  };

  const onSaveS2 = async () => {
    setSaving(true);
    try {
      const payload = cleanPayloadS2();
      const res = await saveSlider2Settings(payload);
      if (!res?.ok) throw new Error("Save failed");

      const refreshed = await getBmaxSettings();
      const s2 = refreshed?.data?.slider2 || {};
      const eq = Array.isArray(s2.equipment) ? s2.equipment : [];
      const ct = Array.isArray(s2.carTypes) ? s2.carTypes : [];
      const cp = Array.isArray(s2.concretePresets) ? s2.concretePresets : [];

      setEquipRows(eq.map((r) => ({ id: r.id, name: r.name || "", equipmentPrice: String(r.equipmentPrice ?? ""), equipmentHourlyRate: String(r.equipmentHourlyRate ?? ""), equipmentMinVolume: String(r.equipmentMinVolume ?? ""), equipmentMaxVolume: String(r.equipmentMaxVolume ?? "") })));
      setCarRows(ct.map((r) => ({ id: r.id, name: r.name || "", vehicleMinCost: String(r.vehicleMinCost ?? ""), vehicleHourlyRate: String(r.vehicleHourlyRate ?? ""), vehicleIncludedHours: String(r.vehicleIncludedHours ?? ""), vehicleMinVolume: String(r.vehicleMinVolume ?? ""), vehicleMaxVolume: String(r.vehicleMaxVolume ?? "") })));
      setPresetRows(cp.map((r) => ({ id: r.id, name: r.name || "", qualityId: String(r.qualityId ?? ""), stoneTypeId: String(r.stoneTypeId ?? ""), reductionId: String(r.reductionId ?? ""), consistencyId: String(r.consistencyId ?? ""), deliveryPer15Min: String(r.deliveryPer15Min ?? ""), priceBufferPercent: String(r.priceBufferPercent ?? ""), pumpingHourlyRate: String(r.pumpingHourlyRate ?? "") })));

      notifyCenter("success", "Slider 2 lagret!", { duration: 2000 });
    } catch (e) {
      console.error(e);
      notifyCenter("error", "Kunne ikke lagre Slider 2.", { duration: 2000 });
    } finally {
      setSaving(false);
    }
  };

  // tabs + indicator
  const tabBarRef = useRef(null);
  const tabRefs = useRef({});

  const updateIndicator = () => {
    const el = tabRefs.current[activeTab];
    if (!el) return;

    setIndicator({
      left: el.offsetLeft,      // ✅ scroll friendly
      width: el.offsetWidth,
    });
  };


  useEffect(() => updateIndicator(), [activeTab]);
  useEffect(() => {
    const onResize = () => updateIndicator();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [activeTab]);

  // ---- Custom Concrete save ----
  const onSaveCustom = async () => {
    setSaving(true);
    try {
      const asPayload = (rows) =>
        rows
          .filter((r) => r._delete || (String(r.name).trim() !== "" || String(r.price).trim() !== ""))
          .map((r) => ({
            id: r.id,
            name: String(r.name ?? "").trim(),
            price: toNum(r.price),
            ...(r._delete ? { _delete: true } : {}),
          }));

      const payload = {
        concrete_quality: asPayload(qualityRows),
        concrete_stone: asPayload(stoneRows),
        concrete_reduction: asPayload(reductionRows),
        concrete_consistency: asPayload(consistencyRows),
      };

      const res = await saveCustomConcrete(payload);
      if (!res?.ok) throw new Error("Save failed");

      const fresh = await getCustomConcrete();
      const d = fresh?.data ?? fresh;
      const asRows = (arr) =>
        (Array.isArray(arr) ? arr : []).map((r) => ({
          id: r.id,
          name: r.name ?? r.title ?? "",
          price: String(r.price ?? r.cost ?? 0),
        }));

      setQualityRows(asRows(d?.concrete_quality));
      setStoneRows(asRows(d?.concrete_stone));
      setReductionRows(asRows(d?.concrete_reduction));
      setConsistencyRows(asRows(d?.concrete_consistency));

      notifyCenter("success", "Spesialtilpasset betong lagret!", { duration: 2000 });
    } catch (e) {
      console.error(e);
      notifyCenter("error", "Kunne ikke lagre spesialtilpasset betong.", { duration: 2000 });
    } finally {
      setSaving(false);
    }
  };

  const updExtraRow = (setter) => (idx, key, val) =>
    setter((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: val };
      return next;
    });

  const onSaveExtra = async () => {
    setSaving(true);
    try {
      const payload = {
        additionalOptions: extraAddOpts.map((r) => ({
          id: r.id,
          price: toNum(r.price),
          active: !!r.active,
        })),
        concreteAdditions: extraConcAdds.map((r) => ({
          id: r.id,
          price: toNum(r.price),
          active: !!r.active,
        })),
        deliveryLeadTimeNote: leadTimeNote,
      };

      // console.log('payload', payload);

      const res = await saveExtraSettings(payload);
      if (!res?.ok) throw new Error("save failed");

      // refresh just the extra section via getBmaxSettings
      const refreshed = await getBmaxSettings();
      const e = refreshed?.data?.extra || {};
      const asExtraRows = (arr) =>
        (Array.isArray(arr) ? arr : []).map((r) => ({
          id: r.id,
          name: r.name ?? "",
          price: String(r.price ?? ""),
          active: !!r.active,
        }));

      setExtraAddOpts(asExtraRows(e.additionalOptions));
      setExtraConcAdds(asExtraRows(e.concreteAdditions));
      setLeadTimeNote(e.deliveryLeadTimeNote ?? "");

      notifyCenter("success", "Ekstra-innstillinger lagret!", { duration: 2000 });
    } catch (e) {
      console.error(e);
      notifyCenter("error", "Kunne ikke lagre ekstra-innstillinger.", { duration: 2000 });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await getSmtpSettings();
        const row = res?.data || null; 

        if (row) {
          setSmtp({
            host: row.host ?? "",
            port: String(row.port ?? ""),
            user: row.user ?? "",
            password: row.password ?? "",
            encryption: row.encryption ?? "SSL",
            fromName: row.fromName ?? "",
            fromEmail: row.fromEmail ?? "",
          });
        } else {
          setSmtp((p) => ({ ...p, encryption: "SSL" }));
        }
      } catch (e) {
        console.error("getSmtpSettings error", e);
      }
    })();
  }, []);

  const onSaveSMTP = async () => {
    setSaving(true);
    try {
      const payload = {
        host: smtp.host,
        port: Number(smtp.port || 0),
        user: smtp.user,
        password: smtp.password,
        encryption: smtp.encryption,
        fromName: smtp.fromName,
        fromEmail: smtp.fromEmail,
      };
      console.log('payload',payload);

      const res = await saveSmtpSettings(payload);
      if (!res?.ok) throw new Error(res?.error || "save_failed");

      notifyCenter("success", "SMTP-innstillinger lagret!", { duration: 2000 });
    } catch (e) {
      console.error(e);
      notifyCenter("error", "Kunne ikke lagre SMTP-innstillinger.", { duration: 2200 });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await getOtherSettings();
        const row = res?.data || null;
        if (row) {
          setAppKeys(row);
          setOther({
            shareApiKey: row.shareApiKey || "",
            googleMapsApiKey: row.googleMapsApiKey || "",
            weatherApiKey: row.weatherApiKey || "",
          });
        }
      } catch (e) {
        console.error("getOtherSettings error", e);
      }
    })();
  }, []);

  const onSaveOther = async () => {
    setSaving(true);
    try {
      const payload = {
        SHARE_API_KEY: other.shareApiKey,
        GOOGLE_MAPS_API_KEY: other.googleMapsApiKey,
        WEATHER_API_KEY: other.weatherApiKey,
      };
      // console.log('payload', payload);

      const res = await saveOtherSettings(payload);
      if (!res?.ok) throw new Error(res?.error || "save_failed");
      localStorage.removeItem("app_keys");
      setAppKeys(res.data);
      notifyCenter("success", "Andre innstillinger lagret!", { duration: 1000 });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      console.error(e);
      notifyCenter("error", "Kunne ikke lagre andre innstillinger.", { duration: 2200 });
    } finally {
      setSaving(false);
    }
  };



  // ---- Slider 1 UI ----
  const renderSlider1 = () => (
    <SoftBox>
      {/* Price per km */}
      <SoftBox
        p={2.25}
        sx={{
          borderRadius: 2,
          background: "#fff",
          boxShadow: "0 8px 22px rgba(15,23,42,.06)",
          border: "1px solid rgba(2,6,23,.06)",
        }}
      >
        <SoftTypography variant="h6" fontWeight="bold" mb={0.5}>
          Pris pr. km.
        </SoftTypography>
        <SoftTypography variant="caption" color="text" mb={2} display="block">
          (grunnpris) + (avstand)*(pris pr. km)
        </SoftTypography>

        <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
          <div>
            <Label>Grunnpris</Label>
            <NumberWithStepper value={distanceRate.basePrice} onChange={(v) => setDR("basePrice", v)} placeholder="Grunnpris" />
          </div>
          <div>
            <Label>Pris pr. km.</Label>
            <NumberWithStepper value={distanceRate.pricePerKm} onChange={(v) => setDR("pricePerKm", v)} placeholder="Pris pr. km." />
          </div>
        </SoftBox>
      </SoftBox>

      <Divider sx={{ my: 3 }} />

      {/* Price based on time */}
      <SoftBox
        p={2.25}
        sx={{
          borderRadius: 2,
          background: "#fff",
          boxShadow: "0 8px 22px rgba(15,23,42,.06)",
          border: "1px solid rgba(2,6,23,.06)",
        }}
      >
        <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <SoftTypography variant="h6" fontWeight="bold">
            Pris basert på tid
          </SoftTypography>
          <Tooltip title="Add row">
            <SoftButton variant="gradient" color="info" size="small" onClick={addTimeRow} startIcon={<AddIcon />}>
              Legg til
            </SoftButton>
          </Tooltip>
        </SoftBox>

        <Box sx={{ display: "grid", gap: 1.25 }}>
          {timeRates.map((row, idx) => {
            const deleted = !!row._delete;
            const canRemove = activeRowsCount() > 1 && !deleted && idx > 0; // first row undeletable
            return (
              <Box
                key={row.id ?? `new-${idx}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "140px 1fr 1fr 44px" },
                  gap: 1,
                  alignItems: "flex-start",
                  opacity: deleted ? 0.5 : 1,
                }}
              >
                <div>
                  <Label>ID</Label>
                  <TextField placeholder="ID" value={row.id ? String(row.id) : "Ny"} InputProps={{ readOnly: true }} fullWidth sx={fieldSx} />
                </div>
                <div>
                  <Label>Leveringstidspunkt (HH:MM)</Label>
                  <TextField
                    placeholder="HH:MM"
                    type="time"
                    value={row.delivery}
                    onChange={(e) => updateTimeRow(idx, "delivery", e.target.value)}
                    inputProps={{ step: 60 }}
                    fullWidth
                    sx={fieldSx}
                    error={row.delivery && !hhmmOk(row.delivery)}
                    helperText={row.delivery && !hhmmOk(row.delivery) ? "Format: 00:00–23:59" : " "}
                  />
                </div>
                <div>
                  <Label>Pris</Label>
                  <NumberWithStepper value={row.price} onChange={(v) => updateTimeRow(idx, "price", v)} placeholder="Pris" />
                </div>
                <Tooltip title={canRemove ? "Fjern" : ""}>
                  <span>
                    <IconButton
                      color="error"
                      onClick={() => removeTimeRow(idx)}
                      size="small"
                      disabled={!canRemove}
                      sx={{ visibility: canRemove ? "visible" : "hidden", mt: "28px" }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      </SoftBox>

      <SoftBox mt={3} display="flex" justifyContent="flex-start" gap={1}>
        <SoftButton variant="gradient" size="small" color="info" onClick={onSaveS1} disabled={loading || saving} startIcon={<SaveIcon />}>
          {saving ? "Saving…" : "Save"}
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );

  // ---- Slider 2 UI (headers + resized columns) ----
  const COL_TPL2 = {
    eq: "90px minmax(220px,0.9fr) repeat(4, minmax(140px,1fr)) 44px",
    car: "90px minmax(220px,0.9fr) repeat(5, minmax(140px,1fr)) 44px",
    pre: "90px minmax(220px,1.4fr) repeat(4, minmax(140px,0.8fr)) minmax(170px,1fr) minmax(140px,0.8fr) minmax(170px,1fr) 44px",
  };

  const renderSlider2 = () => (
    <SoftBox>
      {/* Equipment */}
      <SoftBox p={2.25} sx={{ borderRadius: 2, background: "#fff", boxShadow: "0 8px 22px rgba(15,23,42,.06)", border: "1px solid rgba(2,6,23,.06)" }}>
        <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <SoftTypography variant="h6" fontWeight="bold">
            Type losseutstyr
          </SoftTypography>
          <Tooltip title="Legg til rad">
            <SoftButton variant="gradient" color="info" size="small" onClick={addEquipRow} startIcon={<AddIcon />}>
              Legg til
            </SoftButton>
          </Tooltip>
        </SoftBox>

        <ScrollX minWidth={1120}>
          <GridHeader columns={["ID", "Type", "Pris", "Timepris", "Minstelass (m³)", "Største lass (m³)", ""]} template={COL_TPL2.eq} />

          <Box sx={{ display: "grid", gap: 1.25 }}>
            {equipRows.map((r, i) => {
              const canRemove = equipRows.filter((x) => !x._delete).length > 1 && !r._delete;
              return (
                <Box key={r.id ?? `eq-${i}`} sx={{ display: "grid", gridTemplateColumns: COL_TPL2.eq, gap: 1, opacity: r._delete ? 0.5 : 1 }}>
                  <TextField value={r.id ? String(r.id) : "Ny"} InputProps={{ readOnly: true }} fullWidth sx={compactFieldSx} />
                  <TextField placeholder="Type" value={r.name} onChange={(e) => updEquip(i, "name", e.target.value)} fullWidth sx={compactFieldSx} />
                  <NumberWithStepper value={r.equipmentPrice} onChange={(v) => updEquip(i, "equipmentPrice", v)} placeholder="0" />
                  <NumberWithStepper value={r.equipmentHourlyRate} onChange={(v) => updEquip(i, "equipmentHourlyRate", v)} placeholder="0" />
                  <NumberWithStepper value={r.equipmentMinVolume} onChange={(v) => updEquip(i, "equipmentMinVolume", v)} placeholder="0" />
                  <NumberWithStepper value={r.equipmentMaxVolume} onChange={(v) => updEquip(i, "equipmentMaxVolume", v)} placeholder="0" />
                  <Tooltip title={canRemove ? "Fjern" : ""}>
                    <span>
                      <IconButton color="error" onClick={() => delEquip(i)} size="small" disabled={!canRemove} sx={{ visibility: canRemove ? "visible" : "hidden", mt: "2px" }}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        </ScrollX>
      </SoftBox>

      <Divider sx={{ my: 3 }} />

      {/* Car types */}
      <SoftBox p={2.25} sx={{ borderRadius: 2, background: "#fff", boxShadow: "0 8px 22px rgba(15,23,42,.06)", border: "1px solid rgba(2,6,23,.06)" }}>
        <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <SoftTypography variant="h6" fontWeight="bold">Biltype</SoftTypography>
          <Tooltip title="Legg til rad">
            <SoftButton variant="gradient" color="info" size="small" onClick={addCarRow} startIcon={<AddIcon />}>
              Legg til
            </SoftButton>
          </Tooltip>
        </SoftBox>

        <ScrollX minWidth={1280}>
          <GridHeader columns={["ID", "Type", "Minstekostnad", "Timepris", "Inkl. timer", "Minstevolum", "Maks volum", ""]} template={COL_TPL2.car} />
          <Box sx={{ display: "grid", gap: 1.25 }}>
            {carRows.map((r, i) => {
              const canRemove = carRows.filter((x) => !x._delete).length > 1 && !r._delete;
              return (
                <Box key={r.id ?? `car-${i}`} sx={{ display: "grid", gridTemplateColumns: COL_TPL2.car, gap: 1, opacity: r._delete ? 0.5 : 1 }}>
                  <TextField value={r.id ? String(r.id) : "Ny"} InputProps={{ readOnly: true }} fullWidth sx={compactFieldSx} />
                  <TextField placeholder="Type" value={r.name} onChange={(e) => updCar(i, "name", e.target.value)} fullWidth sx={compactFieldSx} />
                  <NumberWithStepper value={r.vehicleMinCost} onChange={(v) => updCar(i, "vehicleMinCost", v)} placeholder="0" />
                  <NumberWithStepper value={r.vehicleHourlyRate} onChange={(v) => updCar(i, "vehicleHourlyRate", v)} placeholder="0" />
                  <Tooltip title="Pumpemixer: timer som inngår i minstekostnaden. Timeprisen gjelder bare timer utover dette.">
                    <span style={{ display: "block" }}>
                      <NumberWithStepper value={r.vehicleIncludedHours} onChange={(v) => updCar(i, "vehicleIncludedHours", v)} placeholder="0" />
                    </span>
                  </Tooltip>
                  <NumberWithStepper value={r.vehicleMinVolume} onChange={(v) => updCar(i, "vehicleMinVolume", v)} placeholder="0" />
                  <NumberWithStepper value={r.vehicleMaxVolume} onChange={(v) => updCar(i, "vehicleMaxVolume", v)} placeholder="0" />
                  <Tooltip title={canRemove ? "Fjern" : ""}>
                    <span>
                      <IconButton color="error" onClick={() => delCar(i)} size="small" disabled={!canRemove} sx={{ visibility: canRemove ? "visible" : "hidden", mt: "2px" }}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        </ScrollX>
      </SoftBox>

      <Divider sx={{ my: 3 }} />

      {/* Presets */}
      <SoftBox p={2.25} sx={{ borderRadius: 2, background: "#fff", boxShadow: "0 8px 22px rgba(15,23,42,.06)", border: "1px solid rgba(2,6,23,.06)" }}>
        <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <SoftTypography variant="h6" fontWeight="bold">Betong forhåndsinnstillinger</SoftTypography>
          <Tooltip title="Legg til rad">
            <SoftButton variant="gradient" color="info" size="small" onClick={addPresetRow} startIcon={<AddIcon />}>
              LEGG TIL
            </SoftButton>
          </Tooltip>
        </SoftBox>

        <ScrollX minWidth={1280}>
          <GridHeader
            columns={["ID", "Navn", "Kvalitet", "Steintype", "Steinreduksjon", "Konsistens", "Leveringstid (pr. 15 min.)", "Prisbufffer (%)", "Pumpe-timepris", ""]}
            template={COL_TPL2.pre}
          />
          <Box sx={{ display: "grid", gap: 1.25 }}>
            {presetRows.map((r, i) => {
              const canRemove = presetRows.filter((x) => !x._delete).length > 1 && !r._delete;
              return (
                <Box key={r.id ?? `pre-${i}`} sx={{ display: "grid", gridTemplateColumns: COL_TPL2.pre, gap: 1, opacity: r._delete ? 0.5 : 1 }}>
                  <TextField value={r.id ? String(r.id) : "Ny"} InputProps={{ readOnly: true }} fullWidth sx={compactFieldSx} />
                  <TextField placeholder="Navn" value={r.name} onChange={(e) => updPreset(i, "name", e.target.value)} fullWidth sx={compactFieldSx} />
                  <SelectId placeholder="Velg kvalitet" value={r.qualityId} onChange={(v) => updPreset(i, "qualityId", v)} options={concreteOpts.quality} />
                  <SelectId placeholder="Velg steintype" value={r.stoneTypeId} onChange={(v) => updPreset(i, "stoneTypeId", v)} options={concreteOpts.stone} />
                  <SelectId placeholder="Velg reduksjon" value={r.reductionId} onChange={(v) => updPreset(i, "reductionId", v)} options={concreteOpts.reduction} />
                  <SelectId placeholder="Velg konsistens" value={r.consistencyId} onChange={(v) => updPreset(i, "consistencyId", v)} options={concreteOpts.consistency} />
                  <NumberWithStepper value={r.deliveryPer15Min} onChange={(v) => updPreset(i, "deliveryPer15Min", v)} placeholder="1.0" />
                  <NumberWithStepper value={r.priceBufferPercent} onChange={(v) => updPreset(i, "priceBufferPercent", v)} placeholder="0" />
                  <NumberWithStepper value={r.pumpingHourlyRate} onChange={(v) => updPreset(i, "pumpingHourlyRate", v)} placeholder="0" />
                  <Tooltip title={canRemove ? "Fjern" : ""}>
                    <span>
                      <IconButton color="error" onClick={() => delPreset(i)} size="small" disabled={!canRemove} sx={{ visibility: canRemove ? "visible" : "hidden", mt: "2px" }}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        </ScrollX>
      </SoftBox>

      <SoftBox mt={3} display="flex" gap={1}>
        <SoftButton variant="gradient" size="small" color="info" onClick={onSaveS2} disabled={loading || saving} startIcon={<SaveIcon />}>
          {saving ? "Lagrer…" : "Lagre"}
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );

  const renderCustomConcrete = () => (
    <SoftBox>
      <Section title="Betongkvalitet" rows={qualityRows} setRows={setQualityRows} />
      <Section title="Reduksjon" rows={reductionRows} setRows={setReductionRows} />
      <Section title="Steintype" rows={stoneRows} setRows={setStoneRows} />
      <Section title="Betongkonsistens (synk)" rows={consistencyRows} setRows={setConsistencyRows} />

      <SoftBox mt={1.5} display="flex" gap={1}>
        <SoftButton variant="gradient" size="small" color="info" onClick={onSaveCustom} disabled={saving} startIcon={<SaveIcon />}>
          {saving ? "Lagrer…" : "Lagre"}
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );

  const renderExtra = () => {
    // removed ID column; keep layout compact
    const TEMPLATE = "1.3fr 160px 140px"; // Navn | Pris | På/Av

    const Row = ({ rows, setRows }) => {
      const update = updExtraRow(setRows);
      return (
        <Box sx={{ display: "grid", gap: 1.25 }}>
          {rows.map((r, i) => (
            <Box
              key={r.id ?? `extra-${i}`}
              sx={{ display: "grid", gridTemplateColumns: TEMPLATE, gap: 1, alignItems: "center" }}
            >
              <TextField value={r.name} fullWidth sx={compactFieldSx} InputProps={{ readOnly: true }} />
              <NumberWithStepper value={r.price} onChange={(v) => update(i, "price", v)} placeholder="0" />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch
                  checked={!!r.active}
                  onChange={(e) => update(i, "active", e.target.checked)}
                  inputProps={{ "aria-label": "Status" }}
                />
                <SoftTypography variant="caption" sx={{ minWidth: 28 }}>
                  {r.active ? "På" : "Av"}
                </SoftTypography>
              </Box>
            </Box>
          ))}
        </Box>
      );
    };
    Row.propTypes = {
      rows: PropTypes.arrayOf(RowShape).isRequired,
      setRows: PropTypes.func.isRequired,
    };

    return (
      <SoftBox>
        <SoftBox
          p={2.25}
          sx={{ borderRadius: 2, background: "#fff", boxShadow: "0 8px 22px rgba(15,23,42,.06)", border: "1px solid rgba(2,6,23,.06)", mb: 3 }}
        >
          <SoftTypography variant="h6" fontWeight="bold" mb={1}>Tilleggsvalg</SoftTypography>
          <ScrollX minWidth={700}>
            <GridHeader columns={["Navn", "Pris", "Status"]} template={TEMPLATE} />
            <Row rows={extraAddOpts} setRows={setExtraAddOpts} />
          </ScrollX>
        </SoftBox>

        <SoftBox
          p={2.25}
          sx={{ borderRadius: 2, background: "#fff", boxShadow: "0 8px 22px rgba(15,23,42,.06)", border: "1px solid rgba(2,6,23,.06)" }}
        >
          <SoftTypography variant="h6" fontWeight="bold" mb={1}>Betongtillegg</SoftTypography>
          <ScrollX minWidth={700}>
            <GridHeader columns={["Navn", "Pris", "Status"]} template={TEMPLATE} />
            <Row rows={extraConcAdds} setRows={setExtraConcAdds} />
          </ScrollX>
        </SoftBox>

        <SoftBox
          mt={3}
          p={2.25}
          sx={{ borderRadius: 2, background: "#fff", boxShadow: "0 8px 22px rgba(15,23,42,.06)", border: "1px solid rgba(2,6,23,.06)" }}
        >
          <SoftTypography variant="h6" fontWeight="bold" mb={0.5}>Ventetidsmelding</SoftTypography>
          <SoftTypography variant="caption" color="text" display="block" mb={1.5}>
            Vises over datovalget når kunden velger «Velg tidspunkt!» i bestillingen.
            La feltet stå tomt for å skjule meldingen helt.
          </SoftTypography>
          <SoftInput
            value={leadTimeNote}
            placeholder="Det er for tiden 4-5 dagers ventetid på betongleveranser!"
            onChange={(e) => setLeadTimeNote(e.target.value.slice(0, 255))}
          />
        </SoftBox>

        <SoftBox mt={1.5} display="flex" gap={1}>
          <SoftButton
            variant="gradient"
            size="small"
            color="info"
            onClick={onSaveExtra}
            disabled={saving}
            startIcon={<SaveIcon />}
          >
            {saving ? "Lagrer…" : "Lagre"}
          </SoftButton>
        </SoftBox>
      </SoftBox>
    );
  };

  const renderSMTP = () => (
    <SoftBox
      p={2.25}
      sx={{
        borderRadius: 2,
        background: "#fff",
        boxShadow: "0 8px 22px rgba(15,23,42,.06)",
        border: "1px solid rgba(2,6,23,.06)",
      }}
    >

      <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
        <div>
          <Label>Vertsnavn (Host)</Label>
          <TextField
            value={smtp.host}
            onChange={(e) => setSmtp((p) => ({ ...p, host: e.target.value }))}
            placeholder="smtp.domenet.no"
            fullWidth
            sx={fieldSx}
          />
        </div>

        <div>
          <Label>Port</Label>
          <NumberWithStepper
            value={smtp.port}
            onChange={(v) => setSmtp((p) => ({ ...p, port: v }))}
            placeholder="465 / 587"
          />
        </div>

        <div>
          <Label>Brukernavn</Label>
          <TextField
            value={smtp.user}
            onChange={(e) => setSmtp((p) => ({ ...p, user: e.target.value }))}
            placeholder="epost@domenet.no"
            fullWidth
            sx={fieldSx}
            InputProps={{
              sx: {
                '& input': {
                  width: 'calc(100% - 0px) !important', // Adjust input width to prevent text cutoff
                }
              }
            }}
          />
        </div>

        <div>
          <Label>Passord</Label>
          <TextField
            type="text"
            value={smtp.password}
            onChange={(e) => setSmtp((p) => ({ ...p, password: e.target.value }))}
            placeholder="Password"
            fullWidth
            sx={fieldSx}
            InputProps={{
              sx: {
                '& input': {
                  width: 'calc(100% - 0px) !important', // Adjust input width to prevent text cutoff
                }
              }
            }}
          />
        </div>

        <div>
          <Label>Kryptering</Label>
          <TextField
            select
            value={smtp.encryption}
            onChange={(e) => setSmtp((p) => ({ ...p, encryption: e.target.value }))}
            fullWidth
            sx={{ ...fieldSx, cursor: "pointer", userSelect: "none" }}
            // Make whole input clickable to open the menu
            onMouseDown={(e) => {
              e.preventDefault();            // prevent focusing the input only
              setEncryptionOpen(true);       // open the menu
            }}
            SelectProps={{
              open: encryptionOpen,
              onOpen: () => setEncryptionOpen(true),
              onClose: () => setEncryptionOpen(false),
              displayEmpty: true,
            }}
            inputProps={{ readOnly: true }}  // prevent typing into the box
          >
            <MenuItem value="SSL">SSL</MenuItem>
            <MenuItem value="TLS">TLS</MenuItem>
            <MenuItem value="NONE">Ingen</MenuItem>
          </TextField>
        </div>


        <div>
          <Label>Fra-navn</Label>
          <TextField
            value={smtp.fromName}
            onChange={(e) => setSmtp((p) => ({ ...p, fromName: e.target.value }))}
            placeholder="BestillBetong.no"
            fullWidth
            sx={fieldSx}
            InputProps={{
              sx: {
                '& input': {
                  width: 'calc(100% - 0px) !important', // Adjust input width to prevent text cutoff
                }
              }
            }}
          />
        </div>

        <div>
          <Label>Fra-epost</Label>
          <TextField
            value={smtp.fromEmail}
            onChange={(e) => setSmtp((p) => ({ ...p, fromEmail: e.target.value }))}
            placeholder="epost@domenet.no"
            fullWidth
            sx={fieldSx}
            InputProps={{
              sx: {
                '& input': {
                  width: 'calc(100% - 0px) !important', // Adjust input width to prevent text cutoff
                }
              }
            }}
          />
        </div>
      </SoftBox>

      <SoftBox mt={2} display="flex" gap={1}>
        <SoftButton
          variant="gradient"
          size="small"
          color="info"
          onClick={onSaveSMTP}
          disabled={saving}
          startIcon={<SaveIcon />}
        >
          {saving ? "Lagrer…" : "Lagre"}
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );

  const renderOther = () => (
    <SoftBox
      p={2.25}
      sx={{
        borderRadius: 2,
        background: "#fff",
        boxShadow: "0 8px 22px rgba(15,23,42,.06)",
        border: "1px solid rgba(2,6,23,.06)",
      }}
    >
      <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
        <div>
          <Label>Del API-nøkkel</Label>
          <TextField
            value={other.shareApiKey}
            onChange={(e) => setOther((p) => ({ ...p, shareApiKey: e.target.value }))}
            placeholder="xxxxxxxxxxx"
            fullWidth
            sx={fieldSx}
            InputProps={{
              sx: {
                '& input': {
                  width: 'calc(100% - 0px) !important', // Adjust input width to prevent text cutoff
                }
              }
            }}
          />
        </div>

        <div>
          <Label>Google Maps API-nøkkel</Label>
          <TextField
            value={other.googleMapsApiKey}
            onChange={(e) => setOther((p) => ({ ...p, googleMapsApiKey: e.target.value }))}
            placeholder="xxxxxxxxx"
            fullWidth
            sx={fieldSx}
            InputProps={{
              sx: {
                '& input': {
                  width: 'calc(100% - 0px) !important', // Adjust input width to prevent text cutoff
                }
              }
            }}
          />
        </div>

        <div>
          <Label>Vær-API-nøkkel</Label>
          <TextField
            value={other.weatherApiKey}
            onChange={(e) => setOther((p) => ({ ...p, weatherApiKey: e.target.value }))}
            placeholder="xxxxxxxxx"
            fullWidth
            sx={fieldSx}
            InputProps={{
              sx: {
                '& input': {
                  width: 'calc(100% - 0px) !important', // Adjust input width to prevent text cutoff
                }
              }
            }}
          />
        </div>
      </SoftBox>

      <SoftBox mt={2} display="flex" gap={1}>
        <SoftButton
          variant="gradient"
          size="small"
          color="info"
          onClick={onSaveOther}
          disabled={saving}
          startIcon={<SaveIcon />}
        >
          {saving ? "Lagrer…" : "Lagre"}
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );



  const renderTabContent = () => {
    const showLoading = tabLoading || loading;
    switch (activeTab) {
      case "slider1":
        return showLoading ? <SoftTypography variant="body2">Laster…</SoftTypography> : renderSlider1();
      case "slider2":
        return showLoading ? <SoftTypography variant="body2">Laster…</SoftTypography> : renderSlider2();
      case "customConcrete":
        return showLoading ? <SoftTypography variant="body2">Laster…</SoftTypography> : renderCustomConcrete();
      case "extra":
        return showLoading ? <SoftTypography variant="body2">Laster…</SoftTypography> : renderExtra();
      case "smtp":
        return showLoading ? <SoftTypography variant="body2">Laster…</SoftTypography> : renderSMTP();
      case "other":
        return showLoading ? <SoftTypography variant="body2">Laster…</SoftTypography> : renderOther();
      default:
        return null;
    }
  };

  const activeIndex = TABS.findIndex((t) => t.key === activeTab);
  const prevIndex = TABS.findIndex((t) => t.key === prevTab);
  const isForward = activeIndex >= prevIndex;

  const onTabClick = (key) => {
    setPrevTab(activeTab);
    setActiveTab(key);
    setTabLoading(true);
    requestAnimationFrame(() => setTabLoading(false));
  };

  useEffect(() => {
    const el = tabRefs.current[activeTab];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeTab]);



  return (
    <DashboardLayout>
      <SoftBox py={3}>
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 18px 45px rgba(15,23,42,.08)",
            minHeight: "80vh",
            background: "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(246,248,252,0.98))",
            backdropFilter: "blur(6px)",
            "@keyframes slideInLeft": { from: { opacity: 0, transform: "translateX(16px)" }, to: { opacity: 1, transform: "translateX(0)" } },
            "@keyframes slideInRight": { from: { opacity: 0, transform: "translateX(-16px)" }, to: { opacity: 1, transform: "translateX(0)" } },
          }}
        >
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <SoftTypography variant="h6" fontWeight="bold">
              Innstillingsseksjon
            </SoftTypography>
          </SoftBox>

          {/* Tabs */}
          <SoftBox
            ref={tabBarRef}
            display="flex"
            alignItems="center"
            gap={1}
            mb={2}
            sx={{
              position: "relative",
              borderRadius: "8px",
              p: "4px",
              backgroundColor: "#f1f5f9",

              overflowX: "auto",
              overflowY: "hidden",
              flexWrap: "nowrap",
              whiteSpace: "nowrap",
              WebkitOverflowScrolling: "touch",
              
              msOverflowStyle: "none",    // IE/Edge
              touchAction: "pan-x",

              scrollbarWidth: "thin",
              scrollbarColor: "rgba(15,23,42,.35) transparent",
              "&::-webkit-scrollbar": { height: "6px" },
              "&::-webkit-scrollbar-thumb": { borderRadius: "999px", background: "rgba(15,23,42,.35)" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
            }}
          >
            <SoftBox
              sx={{
                position: "absolute",
                top: 4,
                bottom: 4,
                left: indicator.left,
                width: indicator.width,
                borderRadius: "8px",
                background: "linear-gradient(90deg, #0094ff, #0067e5)",
                boxShadow: "0 12px 28px rgba(0,103,229,0.35)",
                transition: "all 320ms cubic-bezier(0.22, 0.61, 0.36, 1)",
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;

              return (
                <Box
                  key={tab.key}
                  ref={(el) => (tabRefs.current[tab.key] = el)}
                  sx={{ flex: "0 0 auto", whiteSpace: "nowrap" }}
                >
                  <SoftButton
                    onClick={() => onTabClick(tab.key)}
                    variant="text"
                    size="small"
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: isActive ? 700 : 500,
                      px: 2.8,
                      py: 0.9,
                      fontSize: "0.95rem",
                      boxShadow: "none",
                      color: isActive ? "#ffffff" : "#111827",
                      backgroundColor: "transparent",
                      whiteSpace: "nowrap",
                      "&:hover": { backgroundColor: isActive ? "transparent" : "#e5e7eb" },
                    }}
                  >
                    {tab.label}
                  </SoftButton>
                </Box>
              );
            })}

          </SoftBox>

          {/* Content */}
          <SoftBox sx={{ mt: 2, minHeight: "260px", display: "flex", alignItems: "flex-start" }}>
            <SoftBox key={activeTab} sx={{ width: "100%", animation: `${isForward ? "slideInLeft" : "slideInRight"} 320ms ease-out` }}>
              {renderTabContent()}
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>

      <Footer />
    </DashboardLayout>
  );
}

export default Settings;
